import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './EditAssessmentModal.css';

function EditAssessmentModal({ assessment, isOpen, onClose, onSave }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [editedReviews, setEditedReviews] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch assessment data when modal opens
  useEffect(() => {
    if (assessment && assessment.email && assessment.cycleId && isOpen) {
      const fetchAssessmentData = async () => {
        try {
          setLoading(true);
          setError(null);

          console.log('Fetching assessment data for editing:', assessment.email, 'cycleId:', assessment.cycleId);

          // Call the API to get all reviews for this email and cycle combination
          const response = await apiService.fetchData(`employee-reviews?type=employee&email=${assessment.email}&cycle_id=${assessment.cycleId}`);

          if (response && response.status === 'success' && response.data && response.data.length > 0) {
            const cycleData = response.data[0];
            const employeeData = cycleData.employees[0];
            const reviews = employeeData.reviews;

            // Create editable review data structure
            const editableReviews = reviews.map(review => ({
              ReviewID: review.ReviewID,
              QuestionID: review.QuestionID,
              Question: review.Question,
              CategoryName: review.CategoryName,
              SelfRating: review.SelfRating,
              SelfComment: review.SelfComment,
              ManagerRating: review.ManagerRating || '',
              ManagerComment: review.ManagerComment || '',
              ActionItem: review.ActionItem || '',
              originalManagerRating: review.ManagerRating,
              originalManagerComment: review.ManagerComment,
              originalActionItem: review.ActionItem
            }));

            setEditedReviews(editableReviews);
            setAssessmentData(employeeData);
          } else {
            setError(response?.message || 'No assessment data found');
          }
        } catch (err) {
          console.error('Error fetching assessment data:', err);
          setError(err.message || 'Failed to load assessment data');
        } finally {
          setLoading(false);
        }
      };

      fetchAssessmentData();
    } else {
      setLoading(false);
    }
  }, [assessment, isOpen]);

  const handleRatingChange = (reviewIndex, rating) => {
    const updatedReviews = [...editedReviews];
    updatedReviews[reviewIndex].ManagerRating = rating;
    setEditedReviews(updatedReviews);
  };

  const handleCommentChange = (reviewIndex, comment) => {
    const updatedReviews = [...editedReviews];
    updatedReviews[reviewIndex].ManagerComment = comment;
    setEditedReviews(updatedReviews);
  };

  const handleActionItemChange = (reviewIndex, actionItem) => {
    const updatedReviews = [...editedReviews];
    updatedReviews[reviewIndex].ActionItem = actionItem;
    setEditedReviews(updatedReviews);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Prepare the updated assessment data
      const updatedAssessment = {
        ...assessment,
        reviews: editedReviews
      };

      await onSave(updatedAssessment);
    } catch (error) {
      console.error('Error in handleSave:', error);
      setError('Failed to save assessment changes');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = () => {
    return editedReviews.some(review =>
      review.ManagerRating !== review.originalManagerRating ||
      review.ManagerComment !== review.originalManagerComment ||
      review.ActionItem !== review.originalActionItem
    );
  };

  // Check if all questions have a rating selected
  const allRatingsComplete = () => {
    return editedReviews.every(review => review.ManagerRating && review.ManagerRating !== '');
  };

  // Check if a specific question's rating or comment has been edited (for showing Edited tag)
  const isQuestionEdited = (review) => {
    return (
      (review.ManagerRating !== review.originalManagerRating) ||
      (review.ManagerComment !== review.originalManagerComment)
    );
  };

  // Get count of questions with missing ratings
  const getMissingRatingsCount = () => {
    return editedReviews.filter(review => !review.ManagerRating || review.ManagerRating === '').length;
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content">
          <div className="loading-container">
            <p>Loading assessment data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !assessmentData) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content">
          <div className="error-container">
            <p>{error || 'Assessment data not found.'}</p>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-assessment-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="associate-info">
            <div className="associate-initials">
              {assessmentData.email.split('@')[0].split(/[._]/).map(n => n[0]).join('').toUpperCase()}
            </div>
            <div className="associate-details">
              <h2>Edit Manager Assessment</h2>
              <p>{assessment.name} | {assessment.role}</p>
              <p className="edit-warning">⚠️ You can edit this assessment only once after meeting scheduling</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Assessment Questions */}
        <div className="assessment-questions">
          <h3>Manager Assessment Questions</h3>
          <div className="questions-list">
            {editedReviews.map((review, index) => (
              <div key={review.ReviewID} className={`question-item ${isQuestionEdited(review) ? 'edited' : ''}`}>
                <div className="question-header">
                  <div className="question-header-top">
                    <div className="question-category">{review.CategoryName}</div>
                    {isQuestionEdited(review) && (
                      <span className="edited-tag">Edited</span>
                    )}
                  </div>
                  <div className="question-text">{review.Question}</div>
                </div>

                {/* Employee Self Assessment Display */}
                <div className="employee-assessment-display">
                  <h4>Employee Self Assessment</h4>
                  <div className="employee-assessment-grid">
                    <div className="employee-rating">
                      <span className="label">Self Rating:</span>
                      <span className="value">{review.SelfRating || 'Not rated'}</span>
                    </div>
                    <div className="manager-rating-display">
                      <span className="label">Manager Rating:</span>
                      <span className="value">{review.ManagerRating || 'Not rated'}</span>
                    </div>
                    <div className="rating-diff">
                      <span className="label">Difference:</span>
                      <span className={`value ${review.SelfRating && review.ManagerRating ? (parseFloat(review.ManagerRating) - parseFloat(review.SelfRating) >= 0 ? 'positive' : 'negative') : ''}`}>
                        {review.SelfRating && review.ManagerRating 
                          ? (parseFloat(review.ManagerRating) - parseFloat(review.SelfRating) > 0 ? '+' : '') + (parseFloat(review.ManagerRating) - parseFloat(review.SelfRating)).toFixed(1)
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="employee-comment">
                      <span className="label">Self Comment:</span>
                      <span className="value">{review.SelfComment || 'No comment provided'}</span>
                    </div>
                  </div>
                </div>

                <div className="question-inputs">
                  <div className="input-group">
                    <label>Manager Rating (1-5):</label>
                    <select
                      value={review.ManagerRating}
                      onChange={(e) => handleRatingChange(index, e.target.value)}
                      className="rating-select"
                    >
                      <option value="">Select Rating</option>
                      <option value="1">1 - Poor</option>
                      <option value="2">2 - Below Average</option>
                      <option value="3">3 - Average</option>
                      <option value="4">4 - Good</option>
                      <option value="5">5 - Excellent</option>
                    </select>
                  </div>

                  <div className="input-group">
                    <label>Manager Comment:</label>
                    <textarea
                      value={review.ManagerComment}
                      onChange={(e) => handleCommentChange(index, e.target.value)}
                      placeholder="Enter your feedback..."
                      className="comment-textarea"
                      rows={3}
                    />
                  </div>

                  <div className="input-group action-item-group">
                    <label>Action Item:</label>
                    <textarea
                      value={review.ActionItem}
                      onChange={(e) => handleActionItemChange(index, e.target.value)}
                      placeholder="Enter action items for improvement..."
                      className="action-item-textarea"
                      rows={4}
                    />
                  </div>

                  <div className="original-values">
                    <small>
                      Original Rating: {review.originalManagerRating || 'Not rated'} |
                      Original Comment: {review.originalManagerComment || 'No comment'} |
                      Original Action Item: {review.originalActionItem || 'None'}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="modal-actions">
          {!allRatingsComplete() && (
            <div className="validation-warning">
              <span className="warning-icon">⚠️</span>
              <span>{getMissingRatingsCount()} question(s) missing rating. All questions must have a rating to save.</span>
            </div>
          )}
          <div className="action-buttons">
            <button className="cancel-btn" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={!hasChanges() || !allRatingsComplete() || isSaving}
              title={!allRatingsComplete() ? 'All questions must have a rating selected' : ''}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditAssessmentModal;